import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface BackendHealthStatus {
  isConnected: boolean;
  isChecking: boolean;
  error: Error | null;
}

const HEALTH_CHECK_INTERVAL = 10000;
const BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const useBackendHealth = () => {
  const pathname = window.location.pathname;
  const [status, setStatus] = useState<BackendHealthStatus>({
    isConnected: true,
    isChecking: true,
    error: null,
  });

  const onboardingRoutes = [
    '/login',
    '/signup',
    '/provisioning',
    '/activate-account',
  ];

  const skipHealthCheck = onboardingRoutes.some(route =>
    pathname.startsWith(route)
  );

  const checkBackendHealth = useCallback(async () => {

    if (skipHealthCheck) return true;

    try {
      await axios.get(`${BASE_URL}/health/`, {
        timeout: 5000,
        headers: {},
        transformRequest: [(data, headers) => {
          if (headers) {
            delete headers.Authorization;
            delete headers['X-Tenant'];
          }
          return data;
        }],
      });

      setStatus({
        isConnected: true,
        isChecking: false,
        error: null,
      });

      return true;
    } catch (error: any) {
      setStatus({
        isConnected: false,
        isChecking: false,
        error,
      });

      return false;
    }
  }, [skipHealthCheck]);

  const retry = useCallback(() => {
    setStatus((prev) => ({ ...prev, isChecking: true }));
    checkBackendHealth();
  }, [checkBackendHealth]);

  useEffect(() => {
    if (skipHealthCheck) {
      setStatus({
        isConnected: true,
        isChecking: false,
        error: null,
      });
      return;
    }

    let isMounted = true;

    const runCheck = async () => {
      if (!isMounted) return;
      await checkBackendHealth();
    };

    runCheck();

    const interval = setInterval(
      runCheck,
      HEALTH_CHECK_INTERVAL,
    );

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [checkBackendHealth, skipHealthCheck]);

  return {
    ...status,
    retry,
  };
};